import { FormEventHandler, useContext, useState } from 'react'
import { Control, FieldErrors, SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import {
  BulkCreateOrderHistoryResponse,
  BulkCreateOrderHistoryVars,
  OrderHistoryApiPath
} from '@/common/api/params/orderHistory'
import { csvToRecords, parseCsv, validateRecords } from '@/common/util/csv'

import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'

import {
  CsvUploadFormSchemaType,
  CsvUploadMode,
  CsvUploadResult,
  ImportResultMessage,
  csvUploadSchema
} from './csvUploadSchema'

const ORDER_CSV_HEADERS = [
  'order_datetime',
  'customer_id',
  'order_number',
  'item_id',
  'unit_price',
  'quantity'
]
const ORDER_CSV_REQUIRED = ['order_datetime', 'customer_id', 'order_number', 'item_id']

const BATCH_SIZE = 500

const processOrderCsv = (text: string) => {
  let rows = parseCsv(text)
  if (rows.length > 0 && rows[0][0] === ORDER_CSV_HEADERS[0]) {
    rows = rows.slice(1)
  }
  return validateRecords(rows, ORDER_CSV_HEADERS, ORDER_CSV_REQUIRED)
}

const handleOrderCsvFile = (
  file: File,
  onSuccess: (validRows: string[][]) => void,
  onError: (errors: ImportResultMessage[]) => void
) => {
  const reader = new FileReader()
  reader.onload = (ev) => {
    const text = ev?.target?.result
    if (text && typeof text === 'string' && text.includes('�')) {
      const reader2 = new FileReader()
      reader2.onload = (ev2) => {
        const result = processOrderCsv(ev2?.target?.result as string)
        if (result.errors.length > 0) {
          onError(result.errors)
        } else {
          onSuccess(result.valid)
        }
      }
      reader2.readAsText(file, 'Shift_JIS')
    } else {
      const result = processOrderCsv(text as string)
      if (result.errors.length > 0) {
        onError(result.errors)
      } else {
        onSuccess(result.valid)
      }
    }
  }
  reader.readAsText(file, 'UTF-8')
}

const handleOrderCsvImport = async (
  file: File,
  siteId: number,
  uploadTrigger: (vars: BulkCreateOrderHistoryVars) => Promise<BulkCreateOrderHistoryResponse>,
  onComplete: (result: Omit<CsvUploadResult, 'mode'>) => void,
  onError: (errors: ImportResultMessage[]) => void
) => {
  const onCsvRead = async (validRows: string[][]) => {
    const records = csvToRecords(validRows, ORDER_CSV_HEADERS)
    const totalResult: { created: number; updated: number; errors: ImportResultMessage[] } = {
      created: 0,
      updated: 0,
      errors: []
    }

    try {
      for (let i = 0; i < records.length; i += BATCH_SIZE) {
        const batch = records.slice(i, i + BATCH_SIZE)
        const response = await uploadTrigger({ site_id: siteId, orders: batch })
        totalResult.created += response.created || 0
        if (response.errors) {
          totalResult.errors.push(
            ...response.errors.map((e) => ({ rowNo: i + e.row, messages: [e.message] }))
          )
        }
      }
      onComplete({
        total: records.length,
        created: totalResult.created,
        updated: totalResult.updated,
        messages: totalResult.errors
      })
    } catch {
      onComplete({
        total: records.length,
        created: totalResult.created,
        updated: totalResult.updated,
        messages: [...totalResult.errors, { messages: ['アップロード中にエラーが発生しました'] }]
      })
    }
  }
  handleOrderCsvFile(file, onCsvRead, onError)
}

export const useOrderCsvUploadApiForm = (
  siteId: number
): {
  result: CsvUploadResult
  control: Control<CsvUploadFormSchemaType>
  errors: FieldErrors<CsvUploadFormSchemaType>
  onSubmit: FormEventHandler<HTMLFormElement>
  reset: () => void
} => {
  const [result, setResult] = useState<CsvUploadResult>({
    created: 0,
    updated: 0,
    total: 0,
    mode: CsvUploadMode.FORM,
    messages: []
  })

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<CsvUploadFormSchemaType>({
    resolver: zodResolver(csvUploadSchema)
  })

  const { trigger: uploadTrigger } = useCallMutation<
    BulkCreateOrderHistoryResponse,
    BulkCreateOrderHistoryVars
  >(OrderHistoryApiPath.BULK_CREATE_ORDER_HISTORY)

  const { showModal, hide } = useContext(ConfirmDialogContext)

  const onSubmit: SubmitHandler<CsvUploadFormSchemaType> = async (data) => {
    showModal({
      message: 'ファイルをアップロードします。よろしいですか？',
      noText: 'キャンセル',
      yesText: 'アップロード',
      buttonColor: 'primary',
      onClickYes: async () => {
        handleOrderCsvImport(
          data.csv,
          siteId,
          uploadTrigger,
          (r) => setResult({ ...r, mode: CsvUploadMode.RESULT }),
          (messages) => setResult((prev) => ({ ...prev, mode: CsvUploadMode.RESULT, messages }))
        )
        hide()
      }
    })
  }

  return {
    result,
    control,
    errors,
    onSubmit: handleSubmit(onSubmit),
    reset: () => {
      reset()
      setResult({ created: 0, updated: 0, total: 0, mode: CsvUploadMode.FORM, messages: [] })
    }
  }
}
